import { AsyncPipe, CommonModule, DatePipe, NgForOf, NgIf, NgOptimizedImage } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DiscountRateInListDto, DiscountRatesApi } from '@buspro/web/apis';
import { AddDiscountRateDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzModalComponent, NzModalContentDirective, NzModalService, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzTableComponent } from 'ng-zorro-antd/table';
import { lastValueFrom } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { EditDiscountRateComponent } from '../edit-discount-rate/edit-discount-rate.component';

@Component({
  selector: 'tenant-discount-rates',
  templateUrl: './discount-rates.component.html',
  styleUrls: ['./discount-rates.component.css'],
  imports: [
    CommonModule,
    NgIf,
    AsyncPipe,
    DatePipe,
    NzPageHeaderModule,
    NgOptimizedImage,
    NzTableComponent,
    NzDividerComponent,
    NgForOf,
    NzModalComponent,
    NzModalTitleDirective,
    NzModalContentDirective,
    ErrorDisplayComponent,
    ReactiveFormsModule,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputGroupComponent,
    DigitOnlyModule,
    NzInputDirective,
    NzFormDirective,
    NzFormItemComponent,
    NzCheckboxModule,
    EditDiscountRateComponent,
  ],
  standalone: true,
})
export class DiscountRatesComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private discountRatesApi = inject(DiscountRatesApi);

  form = this.formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    description: [''],
    code: ['', [Validators.pattern(/^[A-Z0-9_-]*$/), Validators.maxLength(20)]],
    rate: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    applicableToBookings: [true],
    applicableToParcels: [false],
  });
  showAddModal = false;

  discountRates = injectQuery(() => ({
    queryKey: ['discount-rates'],
    queryFn: () => lastValueFrom(this.discountRatesApi.discountRatesControllerGetByCompany()),
  }));
  showEditModal = false;
  selected$$ = new BehaviorSubject<DiscountRateInListDto | null>(null);

  // Standardized error handling
  private readonly formErrorHandler = useFormErrorHandler();
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  constructor(
    private modalService: NzModalService,
    private notification: NzNotificationService,
  ) {}

  ngOnInit(): void {
    this.discountRates.refetch();
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  add() {
    this.clearErrors();

    const payload: AddDiscountRateDto = {
      name: this.form.get('name')!.value!,
      description: this.form.get('description')!.value || undefined,
      code: this.form.get('code')!.value?.toUpperCase() || undefined,
      rate: +this.form.get('rate')!.value!,
      applicableToBookings: this.form.get('applicableToBookings')!.value ?? true,
      applicableToParcels: this.form.get('applicableToParcels')!.value ?? false,
    };

    this.addDiscountRate.mutate(payload);
  }

  delete(data: DiscountRateInListDto) {
    this.modalService.confirm({
      nzTitle: `Delete "${data.name}" discount?`,
      nzContent: `<div>
        <p>You are about to delete the <strong>${data.name}</strong> discount (${data.rate}%).</p>
        <p style="color: red; margin-top: 8px;"><strong>⚠️ This action cannot be undone.</strong></p>
        <p style="color: #666; margin-top: 8px;">Note: Discounts currently in use cannot be deleted.</p>
      </div>`,
      nzOkText: 'Yes, Delete',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => this.deleteDiscountRate.mutate(data.id),
      nzCancelText: 'Cancel',
      nzOnCancel: () => console.log('Cancel'),
    });
  }

  addDiscountRate = injectMutation(() => ({
    mutationFn: (payload: AddDiscountRateDto) =>
      lastValueFrom(this.discountRatesApi.discountRatesControllerAdd({ body: payload })),
    onSuccess: () => {
      this.clearErrors();
      this.form.reset({
        name: '',
        description: '',
        code: '',
        rate: 0,
        applicableToBookings: true,
        applicableToParcels: false,
      });
      this.discountRates.refetch();
      this.showAddModal = false;
      this.notification.success('✅ Success', 'Discount rate has been created successfully');
    },
    onError: (err: any) => {
      const result = this.formErrorHandler.handleFormError(err, { formName: 'tenant-discount-rates-form' });
      this.errorDetails = result.problemDetail ?? null;
      this.showError.set(!!this.errorDetails);
    },
  }));

  deleteDiscountRate = injectMutation(() => ({
    mutationFn: (id: string) => lastValueFrom(this.discountRatesApi.discountRatesControllerDelete({ id })),
    onSuccess: () => {
      this.discountRates.refetch();
      this.notification.success('Discount rate has been deleted', '');
    },
    onError: (err: any) => {
      this.notification.error('Ops! Something went wrong', err.error?.errorMessage || '');
    },
  }));
}
